import { useState, useEffect, useCallback } from 'react'
import Stack from '@mui/joy/Stack'
import Box from '@mui/joy/Box'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import IconButton from '@mui/joy/IconButton'
import Divider from '@mui/joy/Divider'
import Alert from '@mui/joy/Alert'
import CircularProgress from '@mui/joy/CircularProgress'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'

import RefreshIcon from '@mui/icons-material/Refresh'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'

import { CrmRunResult, listRunResults } from 'api/oracleApi'
import { useProcessJobContext } from '../hooks/ProcessJobContext'

// ─── formatters ──────────────────────────────────────────────────────────────

function fmtTokens(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`
    return String(n)
}

function fmtDuration(startIso: string, endIso: string): string {
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
    const secs = Math.round(ms / 1000)
    if (secs < 60) return `${secs}s`
    const mins = Math.floor(secs / 60)
    const rem = secs % 60
    return rem > 0 ? `${mins}m ${rem}s` : `${mins}m`
}

function relativeTime(iso: string | null): string {
    if (!iso) return '—'
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    return `${days}d ago`
}

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ─── stat card ───────────────────────────────────────────────────────────────

function StatCard({
    label,
    value,
    color = 'neutral',
    mono = false,
}: {
    label: string
    value: string | number
    color?: 'neutral' | 'success' | 'danger' | 'warning' | 'primary'
    mono?: boolean
}) {
    const colorMap = {
        neutral: 'text.primary',
        success: 'success.600',
        danger: 'danger.500',
        warning: 'warning.600',
        primary: 'primary.600',
    }
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', px: 2, py: 1.5, flex: 1, minWidth: 80 }}>
            <Typography
                level="h3"
                sx={{
                    lineHeight: 1,
                    fontFamily: mono ? 'monospace' : undefined,
                    color: colorMap[color],
                    fontSize: typeof value === 'string' && value.length > 6 ? '1.25rem' : undefined,
                }}
            >
                {value}
            </Typography>
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10 }}>
                {label}
            </Typography>
        </Sheet>
    )
}

// ─── run result row ───────────────────────────────────────────────────────────

function RunResultRow({ run }: { run: CrmRunResult }) {
    const [expanded, setExpanded] = useState(false)

    const totalProcessed = run.sources.reduce((s, src) => s + src.processedSourceIds.length, 0)
    const totalUnattributed = run.sources.reduce((s, src) => s + src.unattributedSourceIds.length, 0)
    const totalFailed = run.sources.reduce((s, src) => s + src.failedSourceIds.length, 0)
    const totalCompanies = run.sources.reduce((s, src) => s + src.companiesUpdated.length, 0)
    const totalFacts = run.sources.reduce((s, src) => s + src.companiesUpdated.reduce((f, c) => f + c.factsWritten, 0), 0)
    const totalIn = run.sources.reduce((s, src) => s + (src.tokenUsage?.inputTokens ?? 0), 0)
    const totalOut = run.sources.reduce((s, src) => s + (src.tokenUsage?.outputTokens ?? 0), 0)
    const duration = fmtDuration(run.started_at, run.finished_at)

    const hasFailures = totalFailed > 0
    const hasResults = totalProcessed > 0

    return (
        <>
            <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
                <td>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                        {hasFailures ? (
                            <ErrorIcon sx={{ fontSize: 14, color: 'danger.400' }} />
                        ) : hasResults ? (
                            <CheckCircleIcon sx={{ fontSize: 14, color: 'success.400' }} />
                        ) : (
                            <RadioButtonCheckedIcon sx={{ fontSize: 14, color: 'neutral.400' }} />
                        )}
                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.tertiary', fontSize: 10 }}>
                            {run.job_id.replace('job_', '')}
                        </Typography>
                    </Stack>
                </td>
                <td>
                    <Tooltip title={fmtDate(run.started_at)}>
                        <Typography level="body-xs">{relativeTime(run.started_at)}</Typography>
                    </Tooltip>
                </td>
                <td>
                    {run.source_filter ? (
                        <Chip size="sm" variant="soft" color="primary" sx={{ fontSize: 10, height: 18, '--Chip-paddingInline': '6px' }}>
                            {run.source_filter}
                        </Chip>
                    ) : (
                        <Typography level="body-xs" color="neutral">
                            all
                        </Typography>
                    )}
                </td>
                <td>
                    <Stack direction="row" gap={1}>
                        {totalProcessed > 0 && (
                            <Typography level="body-xs" color="success">
                                {totalProcessed} ok
                            </Typography>
                        )}
                        {totalUnattributed > 0 && (
                            <Typography level="body-xs" color="warning">
                                {totalUnattributed} skip
                            </Typography>
                        )}
                        {totalFailed > 0 && (
                            <Typography level="body-xs" color="danger">
                                {totalFailed} fail
                            </Typography>
                        )}
                        {totalProcessed === 0 && totalUnattributed === 0 && totalFailed === 0 && (
                            <Typography level="body-xs" color="neutral">
                                —
                            </Typography>
                        )}
                    </Stack>
                </td>
                <td>
                    <Typography level="body-xs" color={totalFacts > 0 ? 'success' : 'neutral'}>
                        {totalFacts > 0 ? `${totalFacts} fact${totalFacts !== 1 ? 's' : ''}` : '—'}
                    </Typography>
                </td>
                <td>
                    {totalIn > 0 ? (
                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', fontSize: 10, color: 'text.tertiary' }}>
                            {fmtTokens(totalIn)}↑ {fmtTokens(totalOut)}↓
                        </Typography>
                    ) : (
                        <Typography level="body-xs" color="neutral">
                            —
                        </Typography>
                    )}
                </td>
                <td>
                    <Typography level="body-xs" color="neutral" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                        {duration}
                    </Typography>
                </td>
                <td style={{ textAlign: 'right' }}>{expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}</td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={8} style={{ padding: 0 }}>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.level1', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Stack gap={1.5}>
                                {run.sources.map((src) => {
                                    const srcIn = src.tokenUsage?.inputTokens ?? 0
                                    const srcOut = src.tokenUsage?.outputTokens ?? 0
                                    return (
                                        <Stack key={src.sourceType} gap={0.5}>
                                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color="neutral"
                                                    sx={{ fontSize: 10, height: 18, '--Chip-paddingInline': '6px', textTransform: 'capitalize' }}
                                                >
                                                    {src.sourceType}
                                                </Chip>
                                                {src.processedSourceIds.length > 0 && (
                                                    <Typography level="body-xs" color="success">
                                                        {src.processedSourceIds.length} processed
                                                    </Typography>
                                                )}
                                                {src.unattributedSourceIds.length > 0 && (
                                                    <Typography level="body-xs" color="warning">
                                                        {src.unattributedSourceIds.length} unattributed
                                                    </Typography>
                                                )}
                                                {src.failedSourceIds.length > 0 && (
                                                    <Typography level="body-xs" color="danger">
                                                        {src.failedSourceIds.length} failed
                                                    </Typography>
                                                )}
                                                {srcIn > 0 && (
                                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace', fontSize: 10, color: 'text.tertiary' }}>
                                                        {fmtTokens(srcIn)}↑ {fmtTokens(srcOut)}↓ tokens
                                                    </Typography>
                                                )}
                                            </Stack>
                                            {src.companiesUpdated.length > 0 && (
                                                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ pl: 1 }}>
                                                    {src.companiesUpdated.map((c) => (
                                                        <Typography
                                                            key={c.company_id}
                                                            level="body-xs"
                                                            sx={{ fontFamily: 'monospace', fontSize: 10, color: 'text.tertiary' }}
                                                        >
                                                            {c.company_id.slice(0, 8)}… +{c.factsWritten}−{c.factsInvalidated}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            )}
                                        </Stack>
                                    )
                                })}
                                {run.sources.length === 0 && (
                                    <Typography level="body-xs" color="neutral">
                                        No source data recorded.
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </td>
                </tr>
            )}
        </>
    )
}

// ─── main component ───────────────────────────────────────────────────────────

export function DeveloperPanel() {
    const [runResults, setRunResults] = useState<CrmRunResult[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const { job, isRunning, isCancelling, startJob, cancelJob } = useProcessJobContext()

    const handleRunAll = async () => {
        setError('')
        try {
            await startJob()
        } catch (err) {
            setError((err as Error).message || 'Failed to start job')
        }
    }

    const fetchAll = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const runs = await listRunResults(30)
            setRunResults(runs)
        } catch (err) {
            setError((err as Error).message || 'Failed to load data')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void fetchAll()
    }, [fetchAll])

    // ── aggregate token stats across all run results
    const totalInTokens = runResults.reduce((s, r) => s + r.sources.reduce((ss, src) => ss + (src.tokenUsage?.inputTokens ?? 0), 0), 0)
    const totalOutTokens = runResults.reduce((s, r) => s + r.sources.reduce((ss, src) => ss + (src.tokenUsage?.outputTokens ?? 0), 0), 0)
    const totalFactsWritten = runResults.reduce(
        (s, r) => s + r.sources.reduce((ss, src) => ss + src.companiesUpdated.reduce((f, c) => f + c.factsWritten, 0), 0),
        0
    )

    return (
        <Stack gap={3}>
            {/* ── toolbar */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography
                    level="title-sm"
                    sx={{ fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11, color: 'warning.600' }}
                >
                    ● Dev Console
                </Typography>
                <Stack direction="row" gap={1} alignItems="center">
                    <Button
                        size="sm"
                        variant="outlined"
                        startDecorator={<PlayArrowIcon sx={{ fontSize: 15 }} />}
                        loading={isRunning}
                        disabled={isRunning}
                        onClick={handleRunAll}
                    >
                        Run all sources
                    </Button>
                    <Tooltip title="Refresh panel data">
                        <IconButton size="sm" variant="plain" color="neutral" onClick={fetchAll} loading={loading}>
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && (
                <Alert color="danger" size="sm">
                    {error}
                </Alert>
            )}

            {/* ── run history totals */}
            {runResults.length > 0 && (
                <Stack gap={1.5}>
                    <SectionLabel>Run Totals ({runResults.length} recent runs)</SectionLabel>
                    <Stack direction="row" gap={1.5} flexWrap="wrap">
                        <StatCard label="Facts written" value={totalFactsWritten} color={totalFactsWritten > 0 ? 'success' : 'neutral'} />
                        <StatCard label="Tokens in" value={fmtTokens(totalInTokens)} color="neutral" mono />
                        <StatCard label="Tokens out" value={fmtTokens(totalOutTokens)} color="neutral" mono />
                    </Stack>
                </Stack>
            )}

            <Divider />

            {/* ── operations */}
            <Stack gap={1.5}>
                <SectionLabel>Operations</SectionLabel>

                {/* Cancel running job */}
                {isRunning && job && (
                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 1.5, borderColor: 'primary.outlinedBorder' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.500',
                                        animation: 'pulse 1.4s ease-in-out infinite',
                                        '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                                    }}
                                />
                                <Typography level="body-sm">
                                    Job{' '}
                                    <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                                        {job.job_id}
                                    </Typography>{' '}
                                    is running
                                </Typography>
                            </Stack>
                            <Button size="sm" variant="soft" color="danger" loading={isCancelling} onClick={cancelJob}>
                                Cancel
                            </Button>
                        </Stack>
                    </Sheet>
                )}
            </Stack>

            <Divider />

            {/* ── run history */}
            <Stack gap={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <SectionLabel>Run History</SectionLabel>
                    {loading && <CircularProgress size="sm" sx={{ '--CircularProgress-size': '16px' }} />}
                </Stack>

                {runResults.length === 0 && !loading ? (
                    <Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>
                        No run results yet. Trigger a sync to see history.
                    </Typography>
                ) : (
                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                        <Table stickyHeader size="sm" hoverRow sx={{ '& td, & th': { fontSize: 12 } }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 120 }}>Job ID</th>
                                    <th style={{ width: 90 }}>Started</th>
                                    <th style={{ width: 80 }}>Source</th>
                                    <th>Messages</th>
                                    <th style={{ width: 90 }}>Facts</th>
                                    <th style={{ width: 100 }}>Tokens</th>
                                    <th style={{ width: 60 }}>Time</th>
                                    <th style={{ width: 28 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {runResults.map((run) => (
                                    <RunResultRow key={run.job_id} run={run} />
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                )}
            </Stack>
        </Stack>
    )
}

// ─── helper sub-components ────────────────────────────────────────────────────

function SectionLabel({ children, color = 'neutral' }: { children: React.ReactNode; color?: 'neutral' | 'danger' }) {
    return (
        <Typography level="body-xs" color={color} fontWeight="md" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>
            {children}
        </Typography>
    )
}
